import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty } from '../ui/command';
import { Plus, X, Cpu, ChevronsUpDown } from 'lucide-react';

// JS mirror of backend evaluate_computed_logic (io_mapping.py) for live preview.
// Note: numeric ops compare raw values here; backend also applies formula/calibration.
export const evaluateComputedLogicJs = (computedLogic, ioValues, physicalMappings) => {
  const condMatch = (c) => {
    const ioId = String(c.io_id || '').trim();
    if (!ioId) return false;
    const raw = ioValues?.[ioId];
    if (raw === undefined || raw === null) return false;
    const op = c.operator || 'eq';
    const phys = (physicalMappings || []).find(m => String(m.io_id) === ioId);
    if (op === 'eq') {
      const sval = String(c.value ?? '').trim();
      if (String(raw).trim() === sval) return true;
      const fr = parseFloat(raw); const fv = parseFloat(sval);
      if (!Number.isNaN(fr) && !Number.isNaN(fv) && fr === fv) return true;
      if (phys?.value_type === 'Logic') {
        const label = (phys.logic_mapping || {})[String(raw)];
        if (label != null && String(label).trim().toLowerCase() === sval.toLowerCase()) return true;
      }
      return false;
    }
    const num = parseFloat(raw); const target = parseFloat(c.value);
    if (Number.isNaN(num) || Number.isNaN(target)) return false;
    return op === 'gt' ? num > target : op === 'lt' ? num < target : op === 'gte' ? num >= target : op === 'lte' ? num <= target : false;
  };
  const groups = (computedLogic?.groups || []).map(g => {
    const conds = (g.conditions || []).filter(c => String(c.io_id || '').trim());
    if (!conds.length) return null;
    const res = conds.map(condMatch);
    return (g.logic || 'AND') === 'OR' ? res.some(Boolean) : res.every(Boolean);
  }).filter(r => r !== null);
  if (!groups.length) return 0;
  return ((computedLogic?.logic || 'AND') === 'OR' ? groups.some(Boolean) : groups.every(Boolean)) ? 1 : 0;
};

const OPS = [['eq', '='], ['gt', '>'], ['lt', '<'], ['gte', '>='], ['lte', '<=']];
const EMPTY_COND = { io_id: '', operator: 'eq', value: '' };

export const ComputedLogicBuilder = ({ value, onChange, physicalMappings = [], ioOptions = [], currentValues = {}, labels = {} }) => {
  const cl = value && value.groups ? value : { logic: 'AND', groups: [{ logic: 'AND', conditions: [{ ...EMPTY_COND }] }] };
  const [openPicker, setOpenPicker] = React.useState(null);
  const [pickerSearch, setPickerSearch] = React.useState('');
  const ioName = (ioId) => {
    const id = String(ioId);
    const opt = ioOptions.find(o => String(o.io_id) === id);
    if (opt) return opt.default_name || `IO ${id}`;
    const m = physicalMappings.find(pm => String(pm.io_id) === id);
    return m ? m.display_name : `IO ${id}`;
  };
  const needle = pickerSearch.trim().toLowerCase();
  const filteredOptions = (ioOptions.length ? ioOptions : physicalMappings.map(m => ({ io_id: m.io_id, default_name: m.display_name })))
    .filter(o => !needle || [o.io_id, o.default_name].some(v => String(v ?? '').toLowerCase().includes(needle)));
  const set = (next) => onChange(next);
  const setGroup = (gi, patch) => set({ ...cl, groups: cl.groups.map((g, i) => i === gi ? { ...g, ...patch } : g) });
  const setCond = (gi, ci, patch) => setGroup(gi, { conditions: cl.groups[gi].conditions.map((c, i) => i === ci ? { ...c, ...patch } : c) });
  const result = evaluateComputedLogicJs(cl, currentValues, physicalMappings);
  const resultLabel = labels?.[String(result)] ?? (result ? 'TRUE' : 'FALSE');
  const hasLive = currentValues && Object.keys(currentValues).length > 0;

  return (
    <div className="space-y-2 p-3 rounded-md border bg-muted/20" data-testid="computed-logic-builder">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium flex items-center gap-1.5"><Cpu className="w-3.5 h-3.5" /> Logic Formula</Label>
        {cl.groups.length > 1 && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground">Between groups:</span>
            <Select value={cl.logic || 'AND'} onValueChange={(v) => set({ ...cl, logic: v })}>
              <SelectTrigger className="h-6 w-[64px] text-[10px]" data-testid="computed-top-logic"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="AND">AND</SelectItem><SelectItem value="OR">OR</SelectItem></SelectContent>
            </Select>
          </div>
        )}
      </div>
      {ioOptions.length === 0 && physicalMappings.length === 0 && (
        <p className="text-[10px] text-amber-600 dark:text-amber-400">No IO parameters available for this protocol yet.</p>
      )}
      {cl.groups.map((g, gi) => (
        <div key={gi} className="p-2 rounded border bg-background space-y-1.5" data-testid={`computed-group-${gi}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium text-muted-foreground">Group {gi + 1}</span>
            <div className="flex items-center gap-1.5">
              {g.conditions.length > 1 && (
                <Select value={g.logic || 'AND'} onValueChange={(v) => setGroup(gi, { logic: v })}>
                  <SelectTrigger className="h-6 w-[64px] text-[10px]" data-testid={`computed-group-logic-${gi}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="AND">AND</SelectItem><SelectItem value="OR">OR</SelectItem></SelectContent>
                </Select>
              )}
              {cl.groups.length > 1 && (
                <button type="button" className="text-muted-foreground hover:text-destructive" data-testid={`computed-remove-group-${gi}`}
                  onClick={() => set({ ...cl, groups: cl.groups.filter((_, i) => i !== gi) })}><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
          </div>
          {g.conditions.map((c, ci) => {
            const raw = currentValues?.[String(c.io_id)];
            return (
              <div key={ci} className="grid grid-cols-[1fr_64px_1fr_24px] gap-1.5 items-start">
                <Popover modal={true} open={openPicker === `${gi}-${ci}`} onOpenChange={(o) => { setOpenPicker(o ? `${gi}-${ci}` : null); if (o) setPickerSearch(''); }}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="h-7 w-full justify-between text-[11px] font-normal px-2" data-testid={`computed-cond-source-${gi}-${ci}`}>
                      <span className="truncate">{c.io_id ? `${ioName(c.io_id)} (IO ${c.io_id})` : 'Select IO parameter...'}</span>
                      <ChevronsUpDown className="w-3 h-3 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[320px] p-0 z-[9999]" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput placeholder="Search IO id or name..." value={pickerSearch} onValueChange={setPickerSearch} className="h-8 text-xs" data-testid={`computed-cond-source-search-${gi}-${ci}`} />
                      <CommandList className="max-h-[220px]">
                        <CommandEmpty>No IO parameters found.</CommandEmpty>
                        {filteredOptions.slice(0, 200).map(o => (
                          <CommandItem key={String(o.io_id)} value={String(o.io_id)} className="text-xs"
                            onSelect={() => { setCond(gi, ci, { io_id: String(o.io_id) }); setOpenPicker(null); }}>
                            <span className="font-mono text-[10px] w-12 shrink-0 text-muted-foreground">{o.io_id}</span>
                            <span className="truncate">{o.default_name || `IO ${o.io_id}`}</span>
                          </CommandItem>
                        ))}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Select value={c.operator || 'eq'} onValueChange={(v) => setCond(gi, ci, { operator: v })}>
                  <SelectTrigger className="h-7 text-[11px]" data-testid={`computed-cond-op-${gi}-${ci}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{OPS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <div>
                  <Input value={c.value ?? ''} onChange={(e) => setCond(gi, ci, { value: e.target.value })}
                    placeholder="Value or label" className="h-7 text-[11px]" data-testid={`computed-cond-value-${gi}-${ci}`} />
                  {c.io_id && raw !== undefined && raw !== null && (
                    <p className="text-[9px] text-muted-foreground mt-0.5">Now: <span className="font-mono">{String(raw)}</span></p>
                  )}
                </div>
                <button type="button" className="h-7 flex items-center justify-center text-muted-foreground hover:text-destructive" data-testid={`computed-remove-cond-${gi}-${ci}`}
                  onClick={() => g.conditions.length > 1
                    ? setGroup(gi, { conditions: g.conditions.filter((_, i) => i !== ci) })
                    : set({ ...cl, groups: cl.groups.filter((_, i) => i !== gi) })}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
          <Button type="button" variant="ghost" size="sm" className="h-6 text-[10px] px-2" data-testid={`computed-add-cond-${gi}`}
            onClick={() => setGroup(gi, { conditions: [...g.conditions, { ...EMPTY_COND }] })}>
            <Plus className="w-3 h-3 mr-1" /> Condition
          </Button>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <Button type="button" variant="outline" size="sm" className="h-6 text-[10px] px-2" data-testid="computed-add-group"
          onClick={() => set({ ...cl, groups: [...cl.groups, { logic: 'AND', conditions: [{ ...EMPTY_COND }] }] })}>
          <Plus className="w-3 h-3 mr-1" /> Group
        </Button>
        <div className="text-[11px] flex items-center gap-1.5" data-testid="computed-live-preview">
          <span className="text-muted-foreground">Current result:</span>
          {hasLive ? (
            <span className={`font-semibold px-1.5 py-0.5 rounded ${result ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-slate-500/15 text-slate-600 dark:text-slate-300'}`}>
              {resultLabel} ({result})
            </span>
          ) : (
            <span className="text-muted-foreground italic">no live data yet</span>
          )}
        </div>
      </div>
    </div>
  );
};
